import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import BottomNavigation from './BottomNavigation';
import BusinessChatbot from '../chatbot/BusinessChatbot';
import SupportChatBubble from '../support/SupportChatBubble';
import TrialExpiredModal from '../TrialExpiredModal';
import { useTrialCheck } from '../../hooks/useTrialCheck';
import { useAuth } from '../../contexts/AuthContext';
import { CreditCard, Star, Clock, ArrowRight, Mail } from 'lucide-react';

const Layout: React.FC = () => {
  // Initialize sidebar state based on screen size
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Check if window is available (for SSR compatibility)
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1280; // Desktop breakpoint at 1280px
    }
    return true;
  });

  const [, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1280;
    }
    return false;
  });

  const { isTrialExpired, canAccessApp } = useTrialCheck();
  const { tenantData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAiChatbot = location.pathname === '/ai-chatbot';

  // Handle window resize to auto-collapse on smaller screens
  useEffect(() => {
    const handleResize = () => {
      const currentIsDesktop = window.innerWidth >= 1280;
      const isTabletOrMobile = window.innerWidth < 1280;
      
      setIsDesktop(currentIsDesktop);
      
      if (currentIsDesktop) {
        // On desktop, sidebar should stay open by default
        setSidebarCollapsed(false);
      } else if (isTabletOrMobile) {
        // On tablet/mobile, sidebar should be fully closed
        setSidebarCollapsed(true);
      }
    };

    // Set initial state
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleToggleMainSidebar = () => {
      setSidebarCollapsed(prev => !prev);
    };
    window.addEventListener('toggle-main-sidebar', handleToggleMainSidebar);
    return () => window.removeEventListener('toggle-main-sidebar', handleToggleMainSidebar);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavbarAction = (action: string) => {
    // Dispatch a custom event that pages can listen to
    window.dispatchEvent(new CustomEvent('navbar-action', { detail: action }));
  };

  const handleContactSupport = () => {
    window.open('mailto:support@cafelawn.com?subject=No Subscription - Need Help', '_blank');
  };

  const handleViewPlans = () => {
    navigate('/subscription');
  };

  // Show trial expired modal if trial is expired
  if (isTrialExpired) {
    return <TrialExpiredModal />;
  }

  // Show professional welcome screen for new users without subscription
  if (!canAccessApp) {
    const businessName = tenantData?.businessName || 'your business';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4 animate-fade-in">
        <div className="max-w-2xl w-full">
          {/* Main Welcome Card */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 sm:p-12 text-center border border-white/50 dark:border-gray-700/50 animate-fade-in-up">
            {/* Animated Icon */}
            <div className="relative mb-8">
              <div className="w-24 h-24 mx-auto bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <Star className="w-12 h-12 text-white" />
              </div>
              <div className="absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 rounded-full opacity-20 animate-ping"></div>
            </div>
            
            {/* Welcome Message */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Welcome to CafeLawn!
            </h1>
            
            <p className="text-xl sm:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 dark:from-purple-400 dark:via-pink-400 dark:to-indigo-400 font-semibold mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {businessName}
            </p>
            
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 mb-8 leading-relaxed max-w-xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              You're all set! Now let's choose the perfect plan to power your cafe operations and unlock amazing features.
            </p>

            {/* Feature Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 border border-purple-200 dark:border-purple-700/50 transform transition-all duration-300 hover:scale-105">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mb-3 shadow-md">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Free Trial</span>
                <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">No credit card needed</span>
              </div>
              
              <div className="flex flex-col items-center p-4 rounded-xl bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/30 dark:to-pink-800/30 border border-pink-200 dark:border-pink-700/50 transform transition-all duration-300 hover:scale-105">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-pink-600 rounded-full flex items-center justify-center mb-3 shadow-md">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Flexible Plans</span>
                <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">Cancel anytime</span>
              </div>
              
              <div className="flex flex-col items-center p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/30 border border-indigo-200 dark:border-indigo-700/50 transform transition-all duration-300 hover:scale-105">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center mb-3 shadow-md">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Full Features</span>
                <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">All plans included</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-4 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              <button
                onClick={handleViewPlans}
                className="w-full flex items-center justify-center px-8 py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 dark:from-purple-400 dark:via-pink-400 dark:to-indigo-400 text-white font-semibold rounded-xl hover:from-purple-600 hover:via-pink-600 hover:to-indigo-600 dark:hover:from-purple-500 dark:hover:via-pink-500 dark:hover:to-indigo-500 focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] space-x-2"
              >
                <span className="text-lg">Choose Your Plan</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <button
                onClick={handleContactSupport}
                className="w-full flex items-center justify-center px-6 py-3 bg-white/50 dark:bg-gray-700/50 hover:bg-white dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-all duration-200 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transform hover:scale-[1.01] active:scale-[0.99] space-x-2"
              >
                <Mail className="w-5 h-5" />
                <span>Need Help? Contact Support</span>
              </button>
            </div>

            {/* Footer Note */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                Our team is here to help you find the perfect plan for your business needs. Start your free trial today!
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Sidebar */}
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 transition-all duration-300">
        {/* Navbar */}
        {!isAiChatbot && <Navbar onToggle={toggleSidebar} onAction={handleNavbarAction} />}
        
        <main className={`flex-1 min-h-0 bg-gray-50 dark:bg-gray-900 transition-colors duration-200 ${
          isAiChatbot 
            ? 'p-0 pb-0 overflow-hidden' 
            : 'overflow-auto p-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] tablet:p-6 desktop:p-8'
        }`}>
          <Outlet />
        </main>
      </div>
      
      {/* Mobile-only Bottom Navigation */}
      <BottomNavigation />
      
      {/* Global Business Chatbot */}
      <BusinessChatbot />
      
      {/* Global Support Chat Bubble */}
      <SupportChatBubble />
    </div>
  );
};

export default Layout; 